import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronUp,
  CornerDownRight,
  Download,
  File as FileIcon,
  FolderInput,
  ListTree,
  Loader2,
  Paperclip,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import { formatRelative, formatDateTime } from '@/lib/formatters';
import { initialsOf } from '@/components/projects/PeoplePicker';
import { AssigneesPicker } from '@/components/projects/AssigneesPicker';
import { MentionTextarea } from '@/components/projects/MentionTextarea';
import { WorkloadHoursInput } from '@/components/projects/WorkloadHoursInput';
import { formatWorkloadHours } from '@/features/projects/workload';
import {
  PRIORITY_META,
  PRIORITY_ORDER,
  STATUS_OPTION_ORDER,
  type TaskStatusOption,
  statusOptionMeta,
  taskStatusOption,
  taskStatusPatch,
} from '@/features/projects/display';
import { useCreateMentionNotifications } from '@/features/notifications/hooks';
import { useAuth } from '@/app/AuthProvider';
import {
  useAddTaskProjectLink,
  useCreateTask,
  useCreateTaskComment,
  useDeleteTask,
  useDeleteTaskAttachment,
  useDeleteTaskComment,
  useProjects,
  useRemoveTaskProjectLink,
  useSetTaskAssignees,
  useTaskAttachments,
  useTaskAttachmentSignedUrl,
  useTaskComments,
  useTaskProjectLinks,
  useTasks,
  useUpdateTask,
  useUpdateTaskComment,
  useUploadTaskAttachment,
  type TaskAttachmentWithUploader,
  type TaskCommentWithAuthor,
  type TaskWithAssignees,
} from '@/features/projects/hooks';
import type { TaskPriority } from '@/types/database';

interface MemberLite {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
}

const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Drawer
//
// The drawer is mounted at the page level. When `task` is null we still
// render <Sheet> with `open={false}` so the close animation has a frame
// to play out (Radix would otherwise rip the content off mid-transition).
// The "last shown" task is cached in state below so the body keeps
// rendering identifying info until the slide-out finishes.
// ---------------------------------------------------------------------------

export function TaskDetailDrawer({
  open,
  task,
  members,
  disabled,
  onOpenChange,
  onOpenTask,
}: {
  open: boolean;
  task: TaskWithAssignees | null;
  members: MemberLite[];
  disabled: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional escape hatch so the drawer body can navigate between
   * related tasks (parent breadcrumb on a subtask, opening a subtask
   * from the parent's subtask list, …). When undefined we just hide
   * those affordances rather than render unreachable buttons.
   */
  onOpenTask?: (taskId: string) => void;
}) {
  // Keep the last task around so the body doesn't blank out while the
  // sheet animates closed. This matches the pattern Linear / Asana use
  // for their right-side panels.
  const [lastTask, setLastTask] = useState<TaskWithAssignees | null>(task);
  useEffect(() => {
    if (task) setLastTask(task);
  }, [task]);

  const renderTask = task ?? lastTask;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl"
        onOpenAutoFocus={(e) => {
          // We want focus to land on the description box (or the
          // comment composer) rather than the close button, but
          // letting Radix focus the panel is fine for now.
          e.preventDefault();
        }}
        // Radix's DismissableLayer closes the panel on focus-outside,
        // which fires when the browser tab or window loses focus — so
        // people editing a task would lose the panel just by switching
        // tabs. We block *only* that focus-driven dismissal. The X
        // button, Escape, and clicking outside still close the panel
        // normally (blocking the whole interact-outside path was also
        // swallowing the close interaction).
        onFocusOutside={(e) => e.preventDefault()}
      >
        {renderTask ? (
          <TaskDetailContent
            task={renderTask}
            members={members}
            disabled={disabled}
            onOpenTask={onOpenTask}
          />
        ) : (
          <SheetHeader>
            <SheetTitle>Task</SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function TaskDetailContent({
  task,
  members,
  disabled,
  onOpenTask,
}: {
  task: TaskWithAssignees;
  members: MemberLite[];
  disabled: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const updateTask = useUpdateTask();
  const setAssignees = useSetTaskAssignees();

  const persist = (patch: Parameters<typeof updateTask.mutate>[0]['patch']) => {
    updateTask.mutate(
      { id: task.id, projectId: task.project_id, patch },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Update failed';
          toast.error(msg);
        },
      },
    );
  };

  const onAssigneesChange = (nextIds: string[]) => {
    setAssignees.mutate(
      {
        taskId: task.id,
        projectId: task.project_id,
        profileIds: nextIds,
        currentProfileIds: task.assignees.map((a) => a.id),
      },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Update failed';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <>
      <SheetHeader className="gap-3">
        {task.parent_task_id && (
          <ParentBreadcrumb
            parentId={task.parent_task_id}
            projectId={task.project_id}
            onOpenTask={onOpenTask}
          />
        )}
        <div className="flex items-start gap-3 pr-10">
          <Checkbox
            checked={task.is_completed}
            onCheckedChange={(v) => persist({ is_completed: v })}
            disabled={disabled}
            ariaLabel={
              task.is_completed ? 'Mark as not completed' : 'Mark as completed'
            }
            className="mt-1.5"
          />
          <div className="flex-1 space-y-1">
            <TaskNameEditor
              key={task.id}
              value={task.name}
              disabled={disabled}
              onCommit={(name) => persist({ name })}
              completed={task.is_completed}
            />
            <SheetTitle className="sr-only">{task.name}</SheetTitle>
            <div className="text-xs text-muted-foreground">
              {task.is_completed && task.completed_at ? (
                <>Completed {formatRelative(task.completed_at)}</>
              ) : task.updated_at ? (
                <>Updated {formatRelative(task.updated_at)}</>
              ) : null}
            </div>
          </div>
        </div>
      </SheetHeader>

      <SheetBody className="space-y-6">
        <FieldGrid
          task={task}
          members={members}
          disabled={disabled}
          onPersist={persist}
          onAssigneesChange={onAssigneesChange}
        />

        <DescriptionEditor
          key={`desc-${task.id}`}
          taskId={task.id}
          projectId={task.project_id}
          members={members}
          initialValue={task.description ?? ''}
          disabled={disabled}
          onCommit={(description) => persist({ description })}
        />

        {/* Subtasks are an Asana-style flat list under the parent.
            Hidden for child rows (parent_task_id !== null) because we
            don't allow deeper nesting — the migration trigger would
            reject a third-level insert anyway. */}
        {!task.parent_task_id && (
          <SubtasksSection task={task} disabled={disabled} onOpenTask={onOpenTask} />
        )}

        <CommentsSection
          taskId={task.id}
          projectId={task.project_id}
          members={members}
          disabled={disabled}
        />

        <AttachmentsSection
          taskId={task.id}
          projectId={task.project_id}
          disabled={disabled}
        />
      </SheetBody>
    </>
  );
}

// ---------------------------------------------------------------------------
// Parent breadcrumb — only shown on subtask drawers
// ---------------------------------------------------------------------------

function ParentBreadcrumb({
  parentId,
  projectId,
  onOpenTask,
}: {
  parentId: string;
  projectId: string;
  onOpenTask?: (taskId: string) => void;
}) {
  // Reads from the cached project tasks query so the breadcrumb
  // resolves instantly when the user opens a subtask. If the cache
  // somehow doesn't carry the parent (e.g. partial fetch) we hide
  // the breadcrumb rather than render a "loading…" stub.
  const tasksQ = useTasks(projectId);
  const parent = (tasksQ.data ?? []).find((t) => t.id === parentId);
  if (!parent) return null;
  const label = parent.name;
  return (
    <button
      type="button"
      onClick={() => onOpenTask?.(parent.id)}
      disabled={!onOpenTask}
      className={cn(
        'flex w-fit items-center gap-1 rounded text-xs text-muted-foreground',
        onOpenTask && 'hover:text-foreground hover:underline',
      )}
      aria-label={`Open parent task ${label}`}
    >
      <ChevronUp className="h-3.5 w-3.5" />
      <span>Subtask of</span>
      <span className="max-w-xs truncate font-medium text-foreground/80">
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Subtasks section
//
// Flat list of children with an inline checkbox + name editor. We
// reuse the existing tasks cache (so updates from the table flow
// through automatically) and pipe creates / updates / deletes
// through the standard hooks — same optimistic + invalidation path
// the project page uses.
// ---------------------------------------------------------------------------

function SubtasksSection({
  task,
  disabled,
  onOpenTask,
}: {
  task: TaskWithAssignees;
  disabled: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const tasksQ = useTasks(task.project_id);
  const createTask = useCreateTask();
  const [draftName, setDraftName] = useState('');

  const subtasks = useMemo(
    () =>
      (tasksQ.data ?? [])
        .filter((t) => t.parent_task_id === task.id)
        .sort((a, b) => a.position - b.position),
    [tasksQ.data, task.id],
  );

  const submit = async () => {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    try {
      await createTask.mutateAsync({
        projectId: task.project_id,
        parentTaskId: task.id,
        name: trimmed,
      });
      setDraftName('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not add subtask';
      toast.error(msg);
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
          <ListTree className="h-3.5 w-3.5" />
          Subtasks
          {subtasks.length > 0 && (
            <span className="ml-1 text-foreground/80">({subtasks.length})</span>
          )}
        </h3>
      </div>

      {tasksQ.isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : subtasks.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
          No subtasks.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {subtasks.map((s) => (
            <SubtaskRow
              key={s.id}
              subtask={s}
              disabled={disabled}
              onOpen={() => onOpenTask?.(s.id)}
            />
          ))}
        </ul>
      )}

      {!disabled && (
        <div className="flex items-center gap-2">
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Add subtask…"
            className="h-8 text-sm"
            disabled={createTask.isPending}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void submit()}
            disabled={!draftName.trim() || createTask.isPending}
          >
            {createTask.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
      )}
    </section>
  );
}

function SubtaskRow({
  subtask,
  disabled,
  onOpen,
}: {
  subtask: TaskWithAssignees;
  disabled: boolean;
  onOpen?: () => void;
}) {
  const update = useUpdateTask();
  const del = useDeleteTask();
  const [name, setName] = useState(subtask.name);
  useEffect(() => setName(subtask.name), [subtask.name]);

  const persist = (patch: Parameters<typeof update.mutate>[0]['patch']) => {
    update.mutate(
      { id: subtask.id, projectId: subtask.project_id, patch },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Update failed';
          toast.error(msg);
        },
      },
    );
  };

  const onNameBlur = () => {
    const trimmed = name.trim();
    if (trimmed === subtask.name) return;
    if (!trimmed) {
      setName(subtask.name);
      toast.error('Subtask name cannot be empty.');
      return;
    }
    persist({ name: trimmed });
  };

  const onDelete = () => {
    if (!window.confirm(`Delete subtask “${subtask.name}”?`)) return;
    del.mutate(
      { id: subtask.id, projectId: subtask.project_id },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Delete failed';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <li
      className={cn(
        'flex items-center gap-2 px-2 py-1.5',
        subtask.is_completed && 'opacity-60',
      )}
    >
      <Checkbox
        checked={subtask.is_completed}
        onCheckedChange={(v) => persist({ is_completed: v })}
        disabled={disabled}
        ariaLabel={
          subtask.is_completed ? 'Mark as not completed' : 'Mark as completed'
        }
      />
      <CornerDownRight
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
        aria-hidden="true"
      />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={onNameBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        disabled={disabled}
        className={cn(
          'h-7 border-transparent bg-transparent px-2 text-sm hover:border-border focus:border-input',
          subtask.is_completed && 'line-through',
        )}
      />
      {subtask.due_date && (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {subtask.due_date}
        </span>
      )}
      {formatWorkloadHours(subtask.approximate_workload_hours) && (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {formatWorkloadHours(subtask.approximate_workload_hours)}
        </span>
      )}
      {onOpen && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onOpen}
          aria-label={`Open details for ${subtask.name}`}
          title="Open details"
        >
          <ListTree className="h-3.5 w-3.5" />
        </Button>
      )}
      {!disabled && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          disabled={del.isPending}
          aria-label="Delete subtask"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Name editor — large inline title with autosave-on-blur
// ---------------------------------------------------------------------------

function TaskNameEditor({
  value,
  disabled,
  onCommit,
  completed,
}: {
  value: string;
  disabled: boolean;
  onCommit: (next: string) => void;
  completed: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const onBlur = () => {
    const trimmed = draft.trim();
    if (trimmed === value) return;
    if (!trimmed) {
      setDraft(value);
      toast.error('Task name cannot be empty.');
      return;
    }
    onCommit(trimmed);
  };

  return (
    <Textarea
      ref={ref}
      rows={1}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      disabled={disabled}
      className={cn(
        'min-h-9 h-auto resize-none overflow-hidden break-words border-transparent bg-transparent px-2 py-1.5 text-lg font-medium tracking-headline leading-headline hover:border-border focus:border-input',
        completed && 'line-through opacity-60',
      )}
      placeholder="Task name"
    />
  );
}

// ---------------------------------------------------------------------------
// Field grid — Assignee / Due date / Workload / Priority / Status
// ---------------------------------------------------------------------------

function FieldGrid({
  task,
  members,
  disabled,
  onPersist,
  onAssigneesChange,
}: {
  task: TaskWithAssignees;
  members: MemberLite[];
  disabled: boolean;
  onPersist: (patch: Parameters<ReturnType<typeof useUpdateTask>['mutate']>[0]['patch']) => void;
  onAssigneesChange: (nextIds: string[]) => void;
}) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-3 text-sm sm:grid-cols-[120px_minmax(0,1fr)] sm:gap-x-4">
      <Field label="Assignees">
        <AssigneesPicker
          assignees={task.assignees}
          members={members}
          disabled={disabled}
          onChange={onAssigneesChange}
        />
      </Field>

      <Field label="Due date">
        <DatePicker
          value={task.due_date ?? ''}
          onChange={(next) => onPersist({ due_date: next || null })}
          disabled={disabled}
          aria-label="Due date"
          className="h-9"
        />
      </Field>

      <Field label="Workload">
        <WorkloadHoursInput
          value={task.approximate_workload_hours}
          onCommit={(next) =>
            onPersist({ approximate_workload_hours: next })
          }
          disabled={disabled}
          className="h-9"
        />
      </Field>

      <Field label="Priority">
        <Select
          value={task.priority}
          onValueChange={(v) => onPersist({ priority: v as TaskPriority })}
          disabled={disabled}
        >
          <SelectTrigger className="h-9 w-full">
            <span>
              <span
                className={cn(
                  'inline-flex max-w-full items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
                  PRIORITY_META[task.priority].className,
                )}
              >
                {PRIORITY_META[task.priority].label}
              </span>
            </span>
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_ORDER.map((p) => (
              <SelectItem key={p} value={p}>
                {PRIORITY_META[p].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Status">
        <Select
          value={taskStatusOption(task)}
          onValueChange={(v) =>
            onPersist(taskStatusPatch(task, v as TaskStatusOption))
          }
          disabled={disabled}
        >
          <SelectTrigger className="h-9 w-full">
            <span>
              <span
                className={cn(
                  'inline-flex max-w-full items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
                  statusOptionMeta(taskStatusOption(task)).className,
                )}
              >
                {statusOptionMeta(taskStatusOption(task)).label}
              </span>
            </span>
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTION_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {statusOptionMeta(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {/* Multi-homing is parent-task-only — subtasks inherit their
          parent's projects, so the field would just be noise there. */}
      {!task.parent_task_id && (
        <Field label="Projects">
          <ProjectsField task={task} disabled={disabled} />
        </Field>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Projects field — the task's home project plus every project it has
// been added to via task_project_links (migration 86). Members can
// link the task into more projects ("Add to project…") or drop a link
// with the chip's ✕; the home project is fixed here (re-homing lives
// in the row's "Move to project…" menu on the project page).
// ---------------------------------------------------------------------------

function ProjectsField({
  task,
  disabled,
}: {
  task: TaskWithAssignees;
  disabled: boolean;
}) {
  const projectsQ = useProjects();
  const linksQ = useTaskProjectLinks(task.id);
  const addLink = useAddTaskProjectLink();
  const removeLink = useRemoveTaskProjectLink();

  const links = linksQ.data ?? [];
  const homeName =
    task.project?.name ??
    (projectsQ.data ?? []).find((p) => p.id === task.project_id)?.name ??
    'Home project';

  const linkedIds = new Set(links.map((l) => l.project_id));
  const addable = (projectsQ.data ?? []).filter(
    (p) => p.id !== task.project_id && !linkedIds.has(p.id),
  );

  const onAdd = (projectId: string) => {
    addLink.mutate(
      { taskId: task.id, projectId },
      {
        onError: (err) => {
          const msg =
            err instanceof Error ? err.message : 'Could not add to project';
          toast.error(msg);
        },
      },
    );
  };

  const onRemove = (projectId: string) => {
    removeLink.mutate(
      { taskId: task.id, projectId },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Could not remove';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant="muted" className="rounded-full" title="Home project">
        {homeName}
      </Badge>
      {links.map((l) => (
        <Badge
          key={l.project_id}
          variant="outline"
          className="rounded-full pr-1 text-muted-foreground"
          title={`Also appears in “${l.project?.name ?? 'project'}”`}
        >
          {l.project?.name ?? 'Unknown project'}
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(l.project_id)}
              disabled={removeLink.isPending}
              className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-secondary hover:text-foreground"
              aria-label={`Remove from ${l.project?.name ?? 'project'}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        </Badge>
      ))}
      {!disabled && addable.length > 0 && (
        <Select value="" onValueChange={onAdd} disabled={addLink.isPending}>
          <SelectTrigger
            className="h-6 w-auto gap-1 rounded-full border-dashed px-2 text-xs text-muted-foreground hover:text-foreground"
            aria-label="Add to project"
          >
            <span className="inline-flex items-center gap-1">
              {addLink.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <FolderInput className="h-3 w-3" />
              )}
              Add to project
            </span>
          </SelectTrigger>
          <SelectContent>
            {addable.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="flex h-9 items-center text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div>{children}</div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Description editor — autosaves on blur (and on Ctrl/Cmd+Enter)
// ---------------------------------------------------------------------------

function DescriptionEditor({
  taskId,
  projectId,
  members,
  initialValue,
  disabled,
  onCommit,
}: {
  taskId: string;
  projectId: string;
  members: MemberLite[];
  initialValue: string;
  disabled: boolean;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(initialValue);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const notifyMentions = useCreateMentionNotifications();
  useEffect(() => setDraft(initialValue), [initialValue, taskId]);

  const onBlur = () => {
    const next = draft;
    if (next === initialValue) return;
    onCommit(next.trim() ? next : null);
    if (mentionIds.length > 0 && next.trim()) {
      // Best-effort: the description is already saved via onCommit.
      notifyMentions.mutate({
        recipientIds: mentionIds,
        projectId,
        sourceType: 'task_description',
        sourceId: taskId,
        taskId,
        snippet: next.trim().slice(0, 200),
      });
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
          Description
        </h3>
      </div>
      <MentionTextarea
        value={draft}
        onChange={setDraft}
        onMentionsChange={setMentionIds}
        candidates={members}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        placeholder="Add a more detailed description, context, links, or acceptance criteria… Type @ to tag someone."
        disabled={disabled}
        className="min-h-[120px] resize-y"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

function CommentsSection({
  taskId,
  projectId,
  members,
  disabled,
}: {
  taskId: string;
  projectId: string;
  members: MemberLite[];
  disabled: boolean;
}) {
  const commentsQ = useTaskComments(taskId);
  const comments = commentsQ.data ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
          Comments {comments.length > 0 && (
            <span className="ml-1 text-foreground/80">({comments.length})</span>
          )}
        </h3>
      </div>

      {commentsQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : comments.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No comments yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </ul>
      )}

      <CommentComposer
        taskId={taskId}
        projectId={projectId}
        members={members}
        disabled={disabled}
      />
    </section>
  );
}

function CommentRow({ comment }: { comment: TaskCommentWithAuthor }) {
  const { profile } = useAuth();
  const updateComment = useUpdateTaskComment();
  const deleteComment = useDeleteTaskComment();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);

  // The author column can be null if the profile was deleted; render a
  // graceful fallback rather than the raw "null" text.
  const author = comment.author;
  const isOwn = !!profile && comment.author_id === profile.id;
  const isSuper = profile?.role === 'super_admin';
  const canEdit = isOwn;
  const canDelete = isOwn || isSuper;
  const edited =
    new Date(comment.updated_at).getTime() -
      new Date(comment.created_at).getTime() >
    1500;

  const onSaveEdit = async () => {
    const next = draft.trim();
    if (!next) {
      toast.error('Comment cannot be empty.');
      return;
    }
    if (next === comment.body) {
      setEditing(false);
      return;
    }
    try {
      await updateComment.mutateAsync({
        id: comment.id,
        taskId: comment.task_id,
        body: next,
      });
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      toast.error(msg);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteComment.mutateAsync({
        id: comment.id,
        taskId: comment.task_id,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <li className="flex gap-3">
      <Avatar className="h-7 w-7 shrink-0">
        {author?.avatar_url && <AvatarImage src={author.avatar_url} alt="" />}
        <AvatarFallback className="text-[10px]">
          {author ? initialsOf(author) : '?'}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
          <span className="font-medium text-foreground">
            {author?.full_name ?? author?.email ?? 'Unknown user'}
          </span>
          <span
            className="text-muted-foreground"
            title={formatDateTime(comment.created_at)}
          >
            {formatRelative(comment.created_at)}
          </span>
          {edited && (
            <Badge variant="muted" className="px-1 py-0 text-[10px]">
              edited
            </Badge>
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-[72px]"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onSaveEdit}
                disabled={updateComment.isPending}
              >
                {updateComment.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="whitespace-pre-wrap break-words rounded-md bg-secondary/40 px-3 py-2 text-sm text-foreground">
            {comment.body}
          </div>
        )}

        {!editing && (canEdit || canDelete) && (
          <div className="flex gap-3 text-xs text-muted-foreground">
            {canEdit && (
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(true);
                }}
              >
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="hover:text-destructive hover:underline"
                onClick={onDelete}
                disabled={deleteComment.isPending}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function CommentComposer({
  taskId,
  projectId,
  members,
  disabled,
}: {
  taskId: string;
  projectId: string;
  members: MemberLite[];
  disabled: boolean;
}) {
  const create = useCreateTaskComment();
  const notifyMentions = useCreateMentionNotifications();
  const [body, setBody] = useState('');
  const [mentionIds, setMentionIds] = useState<string[]>([]);

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      const comment = await create.mutateAsync({ taskId, body: trimmed });
      if (mentionIds.length > 0) {
        // Best-effort fanout — the comment is already saved, so a
        // notification failure shouldn't roll it back or block the UI.
        notifyMentions.mutate({
          recipientIds: mentionIds,
          projectId,
          sourceType: 'task_comment',
          sourceId: comment.id,
          taskId,
          snippet: trimmed.slice(0, 200),
        });
      }
      setBody('');
      setMentionIds([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not post comment';
      toast.error(msg);
    }
  };

  if (disabled) return null;

  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-2">
      <MentionTextarea
        value={body}
        onChange={setBody}
        onMentionsChange={setMentionIds}
        candidates={members}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder="Write a comment… Type @ to tag someone. (Ctrl/Cmd+Enter to send)"
        className="min-h-[72px] resize-y border-0 px-2 py-1 focus-visible:ring-0"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={submit}
          disabled={!body.trim() || create.isPending}
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {create.isPending ? 'Posting…' : 'Comment'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

function AttachmentsSection({
  taskId,
  projectId,
  disabled,
}: {
  taskId: string;
  projectId: string;
  disabled: boolean;
}) {
  const attachmentsQ = useTaskAttachments(taskId);
  const upload = useUploadTaskAttachment();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const attachments = attachmentsQ.data ?? [];

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Upload sequentially so the optimistic UI ordering stays
      // predictable and we don't slam Storage with parallel writes
      // when the user drops 20 files at once.
      for (const file of Array.from(files)) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast.error(`"${file.name}" exceeds the 100 MB limit.`);
          continue;
        }
        await upload.mutateAsync({ taskId, projectId, file });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
          Attachments {attachments.length > 0 && (
            <span className="ml-1 text-foreground/80">
              ({attachments.length})
            </span>
          )}
        </h3>
        {!disabled && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
              {uploading ? 'Uploading…' : 'Add file'}
            </Button>
            <FolderUploadButton
              size="sm"
              disabled={uploading}
              onFiles={(files) => void handleFiles(files)}
            />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      {attachmentsQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
        </div>
      ) : attachments.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          {disabled ? 'No attachments.' : 'No attachments yet.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <AttachmentRow key={a.id} attachment={a} disabled={disabled} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AttachmentRow({
  attachment,
  disabled,
}: {
  attachment: TaskAttachmentWithUploader;
  disabled: boolean;
}) {
  const { profile } = useAuth();
  const urlQ = useTaskAttachmentSignedUrl(
    attachment.task_id,
    attachment.storage_path,
  );
  const deleteAttachment = useDeleteTaskAttachment();

  const isOwn = !!profile && attachment.uploaded_by === profile.id;
  const isSuper = profile?.role === 'super_admin';
  const canDelete = !disabled && (isOwn || isSuper);

  const sizeLabel = useMemo(
    () => formatBytes(attachment.size_bytes),
    [attachment.size_bytes],
  );

  const onDelete = async () => {
    if (
      !window.confirm(
        `Delete "${attachment.file_name}"? This cannot be undone.`,
      )
    )
      return;
    try {
      await deleteAttachment.mutateAsync({
        id: attachment.id,
        taskId: attachment.task_id,
        storagePath: attachment.storage_path,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  const uploader = attachment.uploader;

  return (
    <li className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground">
        <FileIcon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {attachment.file_name}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {sizeLabel}
          {uploader && (
            <>
              {' · '}
              uploaded by {uploader.full_name ?? uploader.email}
            </>
          )}
          {' · '}
          <span title={formatDateTime(attachment.created_at)}>
            {formatRelative(attachment.created_at)}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {urlQ.data ? (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            asChild
            aria-label="Download"
          >
            <a
              href={urlQ.data}
              target="_blank"
              rel="noreferrer"
              download={attachment.file_name}
            >
              <Download className="h-3.5 w-3.5" />
            </a>
          </Button>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            disabled
            aria-label="Preparing download"
          >
            {urlQ.isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 opacity-50" />
            )}
          </Button>
        )}
        {canDelete && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={deleteAttachment.isPending}
            aria-label="Delete attachment"
          >
            {deleteAttachment.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>
    </li>
  );
}

// Format a byte count as a short human string. We hand-roll this
// instead of pulling another dep because the units are small and the
// edge cases (null, zero, GB) are easy enough inline. `—` for unknown
// matches the rest of the formatter family in lib/formatters.
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(gb < 10 ? 1 : 0)} GB`;
}
